import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface MultiSelectOption {
  value: number;
  label: string;
}

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder,
}: {
  options: MultiSelectOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const selectedLabels = options.filter((o) => selected.includes(o.value));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-left text-sm focus:outline-none focus:ring-1 focus:ring-foreground"
      >
        {selectedLabels.length === 0 ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          selectedLabels.map((o) => (
            <span
              key={o.value}
              className="inline-flex items-center gap-1 rounded-full bg-foreground/10 px-2 py-0.5 text-xs text-foreground"
            >
              {o.label}
            </span>
          ))
        )}
        <ChevronDown
          className={`ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-background shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs italic text-muted-foreground">—</div>
          ) : (
            options.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-surface"
              >
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                {o.label}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}
